// src/services/attendanceService.js
import { db } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { hasApprovedLeaveForDate, getUsersWithApprovedLeaveForDate } from "./leaveService";

const attendanceCollection = collection(db, "attendance");

export const checkIn = async (userId, userName, userRole = "student") => {
  const today = new Date().toISOString().split("T")[0];

  // Check if user has approved leave for today
  const leaveCheck = await hasApprovedLeaveForDate(userId, today);
  if (leaveCheck.hasLeave) {
    throw new Error(`Cannot check in: You have approved leave (${leaveCheck.leaveData.leaveType}) for today`);
  }

  // Check if user already checked in today
  const q = query(
    attendanceCollection,
    where("userId", "==", userId),
    where("date", "==", today),
    where("checkOut", "==", null)
  );

  const snapshot = await getDocs(q);

  if (!snapshot.empty) {
    throw new Error("Already checked in today");
  }

  const payload = {
    userId,
    userName,
    date: today,
    checkIn: serverTimestamp(),
    checkOut: null,
    totalHours: null,
    createdAt: serverTimestamp(),
  };

  const docRef = await addDoc(attendanceCollection, payload);
  
  // Log activity for check-in
  try {
    const { logActivity } = await import("./activityService");
    await logActivity({
      userId,
      userRole,
      text: `${userName} checked in`,
      type: "check_in",
      actionType: "attendance_action",
      metadata: {
        userName,
        date: today,
        checkInTime: new Date().toISOString(),
      },
    });
  } catch (error) {
    console.warn("Failed to log check-in activity:", error);
  }
  
  return { id: docRef.id, ...payload };
};

export const checkOut = async (userId, userRole = "student") => {
  const today = new Date().toISOString().split("T")[0];

  // Find today's check-in record
  const q = query(
    attendanceCollection,
    where("userId", "==", userId),
    where("date", "==", today),
    where("checkOut", "==", null)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    throw new Error("No check-in record found for today");
  }

  const attendanceDoc = snapshot.docs[0];
  const attendanceData = attendanceDoc.data();

  // Calculate total hours
  const checkInTime = attendanceData.checkIn.toDate();
  const checkOutTime = new Date();
  const totalHours = (checkOutTime - checkInTime) / (1000 * 60 * 60); // Convert to hours

  await updateDoc(doc(db, "attendance", attendanceDoc.id), {
    checkOut: serverTimestamp(),
    totalHours: Math.round(totalHours * 100) / 100, // Round to 2 decimal places
    updatedAt: serverTimestamp(),
  });

  // Log activity for check-out
  try {
    const { logActivity } = await import("./activityService");
    await logActivity({
      userId,
      userRole,
      text: `${attendanceData.userName} checked out (${Math.round(totalHours * 100) / 100} hours)`,
      type: "check_out",
      actionType: "attendance_action",
      metadata: {
        userName: attendanceData.userName,
        date: today,
        checkInTime: checkInTime.toISOString(),
        checkOutTime: checkOutTime.toISOString(),
        totalHours: Math.round(totalHours * 100) / 100,
      },
    });
  } catch (error) {
    console.warn("Failed to log check-out activity:", error);
  }

  return {
    id: attendanceDoc.id,
    checkOut: checkOutTime,
    totalHours: Math.round(totalHours * 100) / 100,
  };
};

export const getTodayAttendance = async (userId) => {
  const today = new Date().toISOString().split("T")[0];

  const q = query(
    attendanceCollection,
    where("userId", "==", userId),
    where("date", "==", today)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const attendance = snapshot.docs[0].data();
  return {
    id: snapshot.docs[0].id,
    ...attendance,
    checkIn: attendance.checkIn?.toDate(),
    checkOut: attendance.checkOut?.toDate(),
  };
};

export const getUserAttendance = async (userId, limit = 30) => {
  const q = query(
    attendanceCollection,
    where("userId", "==", userId),
    orderBy("date", "desc")
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.slice(0, limit).map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      checkIn: data.checkIn?.toDate(),
      checkOut: data.checkOut?.toDate(),
    };
  });
};

// Get all attendance records for a user in a specific month/year
export const getUserAttendanceForMonth = async (userId, month, year) => {
  try {
    const q = query(
      attendanceCollection,
      where("userId", "==", userId),
      orderBy("date", "desc")
    );

    const snapshot = await getDocs(q);
    
    return snapshot.docs
      .map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          checkIn: data.checkIn?.toDate(),
          checkOut: data.checkOut?.toDate(),
        };
      })
      .filter(record => {
        const recordDate = new Date(record.date);
        return recordDate.getMonth() + 1 === month && recordDate.getFullYear() === year;
      });
  } catch (error) {
    console.error("Error fetching attendance for month:", error);
    return [];
  }
};

export const getAllAttendance = async (date = null) => {
  let q;

  if (date) {
    q = query(
      attendanceCollection,
      where("date", "==", date),
      orderBy("checkIn", "desc")
    );
  } else {
    q = query(
      attendanceCollection,
      orderBy("date", "desc"),
      orderBy("checkIn", "desc")
    );
  }

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      checkIn: data.checkIn?.toDate(),
      checkOut: data.checkOut?.toDate(),
    };
  });
};

// Mark users as absent (0 hours) if they have approved leave for a specific date
export const markUsersAbsentForLeave = async (date) => {
  try {
    const dateStr = typeof date === 'string' ? date : date.toISOString().split("T")[0];
    
    // Get all users with approved leave for this date
    const usersOnLeave = await getUsersWithApprovedLeaveForDate(dateStr);
    
    if (usersOnLeave.length === 0) {
      return { marked: 0, users: [] };
    }

    const markedUsers = [];

    for (const user of usersOnLeave) {
      // Check if there's already an attendance record for this user and date
      const existingQ = query(
        attendanceCollection,
        where("userId", "==", user.userId),
        where("date", "==", dateStr)
      );

      const existingSnapshot = await getDocs(existingQ);

      if (existingSnapshot.empty) {
        // Create an absent attendance record
        const absentRecord = {
          userId: user.userId,
          userName: user.userName,
          date: dateStr,
          checkIn: null,
          checkOut: null,
          totalHours: 0,
          status: "on_leave",
          leaveType: user.leaveType,
          leaveReason: user.reason,
          leaveId: user.leaveId,
          createdAt: serverTimestamp(),
          isAutoGenerated: true,
        };

        await addDoc(attendanceCollection, absentRecord);
        markedUsers.push(user);
      }
    }

    return { marked: markedUsers.length, users: markedUsers };
  } catch (error) {
    console.error("Error marking users absent for leave:", error);
    throw error;
  }
};

// Get comprehensive attendance including users on leave
export const getComprehensiveAttendance = async (date) => {
  try {
    // First mark users absent if they have approved leave
    await markUsersAbsentForLeave(date);
    
    // Then get all attendance records
    return await getAllAttendance(date);
  } catch (error) {
    console.error("Error getting comprehensive attendance:", error);
    throw error;
  }
};

// Get monthly attendance statistics for all employees
export const getMonthlyAttendance = async (year, month) => {
  try {
    // Get first and last day of month
    const firstDay = new Date(year, month - 1, 1).toISOString().split("T")[0];
    const lastDay = new Date(year, month, 0).toISOString().split("T")[0];

    // Query attendance records for the month
    const q = query(
      attendanceCollection,
      where("date", ">=", firstDay),
      where("date", "<=", lastDay),
      orderBy("date", "desc"),
      orderBy("userId", "asc")
    );

    const snapshot = await getDocs(q);
    const records = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Get approved leaves for the month
    const { getApprovedLeavesForMonth } = await import("./leaveService");
    const approvedLeaves = await getApprovedLeavesForMonth(year, month);

    // Create a map of leaves by userId and date
    const leaveMap = {};
    approvedLeaves.forEach((leave) => {
      if (leave.status === "approved") {
        // If leave spans multiple days, add each day
        const startDate = new Date(leave.startDate);
        const endDate = new Date(leave.endDate);
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          const dateStr = d.toISOString().split("T")[0];
          if (!leaveMap[leave.userId]) {
            leaveMap[leave.userId] = {};
          }
          leaveMap[leave.userId][dateStr] = leave.leaveType;
        }
      }
    });

    // Group by user and calculate statistics
    const userStats = {};
    records.forEach((record) => {
      if (!userStats[record.userId]) {
        userStats[record.userId] = {
          userId: record.userId,
          userName: record.userName,
          present: 0,
          absent: 0,
          onLeave: 0,
          totalHours: 0,
          records: [],
          leaves: [],
        };
      }

      userStats[record.userId].records.push(record);

      // Check if this date has an approved leave
      const isOnLeave = leaveMap[record.userId] && leaveMap[record.userId][record.date];

      if (isOnLeave) {
        userStats[record.userId].onLeave++;
        if (!userStats[record.userId].leaves.includes(isOnLeave)) {
          userStats[record.userId].leaves.push(isOnLeave);
        }
      } else if (record.checkIn) {
        userStats[record.userId].present++;
        if (record.totalHours) {
          userStats[record.userId].totalHours += record.totalHours;
        }
      } else {
        userStats[record.userId].absent++;
      }
    });

    // Add leave-only days for users who are on leave but have no attendance record
    Object.keys(leaveMap).forEach((userId) => {
      if (!userStats[userId]) {
        // Find user name from records or set to "Unknown"
        const userRecord = records.find((r) => r.userId === userId);
        userStats[userId] = {
          userId,
          userName: userRecord ? userRecord.userName : "Unknown",
          present: 0,
          absent: 0,
          onLeave: 0,
          totalHours: 0,
          records: [],
          leaves: [],
        };
      }

      // Count leave days
      const leaveDays = Object.keys(leaveMap[userId]).length;
      userStats[userId].onLeave = leaveDays;
    });

    return {
      month,
      year,
      firstDay,
      lastDay,
      userStats: Object.values(userStats),
      totalRecords: records,
    };
  } catch (error) {
    console.error("Error getting monthly attendance:", error);
    throw error;
  }
};
